import React from 'react';

import cloneDeep from 'lodash/cloneDeep';

import Checkbox from '@material-ui/core/Checkbox';
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import { UiButton } from '../../ui/ui-button';

import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';

const styles = () =>
  createStyles({
    experienceRow: {
      '&:last-child': {
        // This is our gray color
        borderBottom: `1px solid #737272`,
      },
    },
    cancelIcon: {
      verticalAlign: 'middle',
      cursor: 'pointer',
      fontSize: '20px',
    },
  });

type ExperienceWithName = {
  jobId: number | null;
  jobExperienceId: number | null;
  experienceName: string;
  reactListId: string;
  isActive: boolean;
  isRequired: boolean;
};

interface Props
  extends WithStyles<typeof styles>,
    ReduxFormFieldProps<Array<Partial<ExperienceWithName>>> {
  jobId: number;
  suggestions?: string;
}

type State = {
  experiences: Array<Partial<ExperienceWithName>>;
  newExperienceName: string | null;
  newExperienceRequired: boolean;
};

const getRandomArbitrary = (min: number, max: number) =>
  Math.round(Math.random() * (max - min) + min);
class JobExperienceReduxFormFieldComponent extends React.PureComponent<Props, State> {
  state: State = {
    experiences: [],
    newExperienceName: null,
    newExperienceRequired: false,
  };

  componentDidMount() {
    this.setState({
      experiences: this.props.input.value,
    });
  }

  componentDidUpdate() {
    if (this.props.input.value !== this.state.experiences) {
      this.setState({ experiences: this.props.input.value });
    }
  }

  onChange = (experiences: Array<Partial<ExperienceWithName>>) => {
    this.setState({
      experiences,
    });
    this.props.input.onChange(experiences);
  };

  removeExperience = (index: number) => () => {
    if (index < 0 || index >= this.state.experiences.length) {
      return;
    }
    // Create a new copy before we modify it
    const tempExperience = cloneDeep(this.state.experiences);
    const item = this.state.experiences[index];
    /*
    If this is an existing item, we don't want to actually delete it,
    just set it to inactive so we don't lose data.
     */
    if (item.jobExperienceId) {
      tempExperience[index].isActive = false;
      this.onChange(tempExperience);
      return;
    }

    // This is an item that was just added and then removed, just kill it from the list.
    tempExperience.splice(index, 1);
    this.onChange(tempExperience);
  };

  updateIsRequired = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (index < 0 || index >= this.state.experiences.length) {
      return;
    }
    // Create a new copy before we modify it
    const tempExperience = this.state.experiences.slice();
    tempExperience[index].isRequired = event.target.checked;
    this.onChange(tempExperience);
  };

  addExperience = () => {
    const { newExperienceName, newExperienceRequired } = this.state;
    if (!newExperienceName) {
      return;
    }
    const tempExperience = this.state.experiences.slice();
    tempExperience.push({
      jobExperienceId: null,
      jobId: this.props.jobId,
      experienceName: newExperienceName,
      reactListId: `new-${getRandomArbitrary(0, 100000)}`,
      isActive: true,
      isRequired: newExperienceRequired,
    });

    this.onChange(tempExperience);
    this.setState({
      newExperienceName: null,
      newExperienceRequired: false,
    });
  };

  keyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Enter key
    if (e.keyCode === 13) {
      e.preventDefault();
      this.addExperience();
    }
  };

  handleChange =
    (key: string) => (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const { value, type } = event.target;

      // Checked is only in an HTMLInputElement
      const checked = 'checked' in event.target ? event.target.checked : false;

      // Don't allow names beyond 100 characters
      // Typing this is tough. You really should only be allowed to use properties that are
      // strings or booleans as the key here.
      this.setState({
        [key]: type === 'checkbox' ? checked : value.slice(0, 100),
      } as any);
    };

  handleSuggestionClick = (suggestion: string) => () => {
    this.setState(
      {
        newExperienceName: suggestion,
        newExperienceRequired: false,
      },
      this.addExperience,
    );
  };

  render() {
    const { classes, suggestions } = this.props;
    const { experiences } = this.state;

    const downcasedExperiences = experiences.map((e) => e.experienceName?.toLowerCase() || '');
    const unfilteredSuggestions = suggestions ? suggestions.split(';') : [];
    const filteredSuggestions = unfilteredSuggestions.filter(
      (s) => !downcasedExperiences.includes(s.toLowerCase()),
    );

    return (
      <>
        <Table>
          <TableHead className="u-border-bottom u-border-gray u-margin-top u-background-lightGray">
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Required</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {experiences.length > 0 &&
              experiences.map(
                (e, i) =>
                  e.isActive && (
                    <TableRow
                      key={`job-experience-${
                        e.jobExperienceId || e.reactListId || `new-${getRandomArbitrary(0, 100000)}`
                      }`}
                      className={classes.experienceRow}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          <span className="u-verticalAlign-middle">{e.experienceName}</span>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          id="newExperienceRequired"
                          checked={e.isRequired}
                          onChange={this.updateIsRequired(i)}
                        />
                      </TableCell>
                      <TableCell className="u-textAlign-center">
                        <MaterialDesignIcon
                          iconName={ICON_NAMES.DELETE_FOREVER}
                          onClick={this.removeExperience(i)}
                          className={`u-margin ${classes.cancelIcon}`}
                        />
                      </TableCell>
                    </TableRow>
                  ),
              )}
            <TableRow>
              <TableCell>
                <TextField
                  id="newExperienceName"
                  label="New Experience"
                  value={this.state.newExperienceName || ''}
                  onChange={this.handleChange('newExperienceName')}
                  margin="normal"
                  className="u-margin u-padding-right-2x u-verticalAlign-middle u-width-100"
                  onKeyDown={this.keyDown}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  id="newExperienceRequired"
                  checked={this.state.newExperienceRequired}
                  onChange={this.handleChange('newExperienceRequired')}
                />
              </TableCell>
              <TableCell className="u-textAlign-center">
                <UiButton onClick={this.addExperience} variant="secondary" size="sm">
                  Add
                </UiButton>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {filteredSuggestions.length ? (
          <div className="u-flex-center u-margin-top">
            <div>Suggestions</div>
            <div>
              {filteredSuggestions.map((suggestion) => (
                <UiButton
                  key={suggestion}
                  className="u-margin-left u-margin-top-xs u-margin-bottom-xs u-whiteSpace-nowrap"
                  onClick={this.handleSuggestionClick(suggestion)}
                  size="xs"
                  variant="tertiary"
                >
                  {suggestion}
                </UiButton>
              ))}
            </div>
          </div>
        ) : null}
      </>
    );
  }
}

export const JobExperienceReduxFormField = withStyles(styles)(JobExperienceReduxFormFieldComponent);
